import { useState } from "react";
import { ActionHandler } from "@/components/UI/tools";
import { ArrowDown, Add_new } from "@/components/UI/svgs";
import { GallerySection, Placeholder, Specimen, Stage, noop } from "../galleryKit";
import { StatusToolBox } from "@/app/workspace/chat/chatWindow/chatMessage";

const TurnsDemo = () => (
    <div className="flex flex-col gap-1 max-w-[40rem]">
        <div className="chat-turn">
            <div className="chat-row">
                <div className="chat-message">
                    <div className="chat-message-body chat-message-user p-1">User message</div>
                </div>
            </div>
        </div>
        <div className="chat-turn chat-turn-assistant">
            <div className="chat-row">
                <div className="chat-message">
                    <div className="chat-message-body p-1">Assistant message</div>
                </div>
            </div>
            <div className="flex flex-row gap-1">
                <span className="ghost-action !opacity-60">replay</span>
                <span className="ghost-action !opacity-60">copy</span>
            </div>
        </div>
        <div className="chat-turn">
            <div className="chat-row">
                <div className="chat-message">
                    <div className="chat-message-body chat-message-user chat-message-preprompt p-1">Pre-prompt message</div>
                    <div className="chat-message-body chat-message-user chat-message-identity p-1">Identity message</div>
                </div>
            </div>
        </div>
    </div>
);

const ToolStatusDemo = () => (
    <div className="flex flex-col gap-[.35rem]">
        <div className="flex flex-col max-w-[28rem]">
            <StatusToolBox status="streaming" label="Running" intent="what the agent is doing" />
            <StatusToolBox status="success" label="Succeeded" intent="a long intent that does not fit on one line truncates with an ellipsis" />
            <StatusToolBox status="error" label="Failed" />
        </div>
        <div className="chat-turn chat-turn-assistant !items-start p-1"><span className="skill-chip">Skill chip</span></div>
    </div>
);

const ComposerDemo = () => {
    const [slash, setSlash] = useState(true);
    const [active, setActive] = useState(0);
    return (
        <Stage height="20rem">
            <div className="flex flex-col justify-end h-full p-1">
                <div className="composer-dock !static !h-fit">
                    <div className="relative flex flex-col w-full gap-[.35rem]">
                        <div className={`scroll-fab ${slash ? "scroll-fab-hidden" : ""}`}><ArrowDown size={20} /></div>
                        {slash && (
                            <div className="slash-menu">
                                {["/command-one", "/command-two", "/command-three"].map((c, i) => (
                                    <button key={c} className={`slash-option ${active === i ? "slash-option-active" : ""}`} onMouseEnter={() => setActive(i)}>{c}</button>
                                ))}
                            </div>
                        )}
                        <div className="thread-status-wrap">
                            <div className="thread-queued">
                                <small className="thread-queued-label">queued</small>
                                <p className="thread-queued-text">Also check the latest note.</p>
                            </div>
                            <div className="thread-status">
                                <div className="loader w-1 h-1" />
                                <span className="thread-status-label">Working…</span>
                            </div>
                        </div>
                        <div className="composer flex-col !gap-0">
                            <div className="flex flex-wrap gap-1 p-1 pb-0">
                                <div className="attachment">
                                    <button className="attachment-label">attachment-name.pdf</button>
                                    <span className="attachment-meta">120 KB</span>
                                    <span className="attachment-dismiss">×</span>
                                </div>
                            </div>
                            <div className="flex flex-row items-end gap-1 w-full">
                                <div className="composer-editor p-1"><span className="serif text-black/40">Write a message…</span></div>
                                <ActionHandler funq={() => setSlash(!slash)} className="btn-ghost" title="Toggle slash menu"><Add_new /></ActionHandler>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Stage>
    );
};

const SidebarDemo = () => (
    <div className="flex flex-row gap-2">
        <div className="flex flex-col gap-[.35rem] w-[11rem]">
            <input className="sidebar-filter" placeholder="Filter…" />
            <span className="sidebar-caption">Sidebar caption</span>
            <div className="timeline-rail h-3"><span className="sidebar-caption">2026-01-01</span></div>
            <div className="timeline-rail h-2" />
        </div>
        <div className="relative h-[10rem] w-[3rem]">
            <div className="edge-toggle !flex !h-full"><ArrowDown size={16} /></div>
        </div>
        <Stage height="10rem"><div className="minimap !right-0" /></Stage>
    </div>
);

const EditorTabsDemo = () => {
    const [tab, setTab] = useState(0);
    return (
        <div className="relative panel-elevated h-[8rem] p-1">
            <Placeholder label="editor body" className="h-[4rem]" />
            <div className="editor-tabs">
                {["Tab one", "Tab two", "Tab three"].map((t, i) => (
                    <ActionHandler key={t} funq={() => setTab(i)} selected={tab === i} className="btn-ghost">{t}</ActionHandler>
                ))}
            </div>
        </div>
    );
};

const TranscriptDemo = () => (
    <div className="relative flex flex-col max-w-[36rem] h-[16rem] overflow-y-auto rounded">
        <div className="transcript-header">
            <div className="status-strip">
                <span className="small">Status</span>
                <span className="status-strip-value">00:42</span>
                <div className="level-meter"><div className="level-meter-fill" style={{ width: "60%" }} /></div>
            </div>
        </div>
        <div className="transcript-body flex flex-col gap-[.25rem] px-[.5rem]">
            {["Recognised phrase.", "A cited phrase."].map((t, i) => (
                <div key={t} className={`transcript-phrase ${i === 1 ? "transcript-phrase-active" : ""}`}>
                    {i === 1 && <span className="transcript-pause">… 1.20 s</span>}
                    <span className="transcript-attr">
                        <span className="transcript-attr-speaker">Speaker {i + 1}</span>
                        <span>{(2.4 + i).toFixed(2)} s</span>
                    </span>
                    <span>{t}</span>
                </div>
            ))}
            <span className="partial-text">partial text…</span>
        </div>
    </div>
);

const RecorderDemo = () => (
    <div className="flex flex-col gap-1">
        <div className="flex flex-row items-center gap-1">
            <div className="record-btn record-btn-hover w-3 h-3">
                <div className="record-btn-ring record-btn-ring-idle border-2" />
                <div className="record-btn-dot-idle w-2 h-2" />
            </div>
            <div className="record-btn w-3 h-3">
                <div className="record-btn-ring record-btn-ring-live border-4" />
                <div className="record-btn-level w-1 h-1" />
            </div>
            <div className="record-btn record-btn-disabled w-3 h-3">
                <div className="record-btn-dot-disabled w-1 h-1" />
            </div>
        </div>
        <div className="flex flex-row items-center gap-1">
            <ActionHandler funq={noop} className="btn-record btn-sm">Start</ActionHandler>
            <ActionHandler funq={noop} className="btn-danger btn-sm">Stop</ActionHandler>
            <span className="recorder-status">Ready</span>
            <span className="recorder-status recorder-status-live">Recording…</span>
        </div>
        <p className="transcript-quote max-w-[28rem]">“What the recorder heard.”</p>
    </div>
);

export const ChatSection = () => (
    <GallerySection id="chat" title="Chat" intro="The conversation column, the composer and the side panels around it.">
        <Specimen
            name="Turn and message"
            classes={["chat-turn", "chat-turn-assistant", "chat-row", "chat-message", "chat-message-body", "chat-message-user", "chat-message-preprompt", "chat-message-identity", "ghost-action"]}
            usage="A turn groups the messages from one role; assistant turns sit on elevated. User messages read in serif. ghost-action reveals replay / copy on hover."
            demo={() => <TurnsDemo />}
        />
        <Specimen
            name="Tool status and skill chip"
            classes={["tool-status", "tool-status-success", "tool-status-error", "status-pulse", "skill-chip"]}
            usage="Rendered by StatusToolBox / ToolStatusIcon (chatMessage.tsx), the same components the chat uses: one filled circle per state (pulsing while running, green on success, red on error). Text truncates to one line with the full text as hover title. skill-chip announces an active skill."
            demo={() => <ToolStatusDemo />}
        />
        <Specimen
            name="Composer"
            classes={["composer-dock", "composer-dock-inner", "composer", "composer-editor", "thread-status-wrap", "thread-status", "thread-status-label", "thread-queued", "thread-queued-label", "thread-queued-text", "composer-stop", "slash-menu", "slash-option", "slash-option-active", "slash-empty", "attachment", "attachment-label", "attachment-meta", "attachment-dismiss", "scroll-fab", "scroll-fab-hidden"]}
            usage="Pinned to the bottom of the chat column. The status bar appears above it while the assistant works; the slash menu opens above it on '/'."
            demo={() => <ComposerDemo />}
        />
        <Specimen
            name="Side panels"
            classes={["chat-sidebar", "sidebar-filter", "sidebar-caption", "timeline-rail", "edge-toggle", "minimap"]}
            usage="Columns either side of the conversation hold nav-items; the right one draws a timeline. On mobile, edge-toggle swipes a panel in."
            demo={() => <SidebarDemo />}
        />
        <Specimen
            name="Editor tabs"
            classes={["editor-tabs", "panel-elevated"]}
            usage="Tabs pinned to the bottom of the object editor, one per open object."
            demo={() => <EditorTabsDemo />}
        />
        <Specimen
            name="Transcript"
            classes={["transcript-header", "status-strip", "status-strip-value", "transcript-body", "transcript-phrase", "transcript-phrase-active", "transcript-attr", "transcript-attr-speaker", "transcript-pause", "partial-text", "level-meter", "level-meter-fill"]}
            usage="Live transcription view: sticky status header, the serif transcript, and an input-level meter (width set inline from the signal)."
            demo={() => <TranscriptDemo />}
        />
        <Specimen
            name="Recorder"
            classes={["record-btn", "record-btn-hover", "record-btn-ring", "record-btn-ring-idle", "record-btn-ring-live", "record-btn-dot-idle", "record-btn-level", "record-btn-disabled", "record-btn-dot-disabled", "btn-record", "recorder-status", "recorder-status-live", "transcript-quote"]}
            usage="Round record control (idle, live, no microphone) and the speaker-profile recorder's start button, status and preview. Sizes, ring width and level scale are set inline from the audio signal."
            demo={() => <RecorderDemo />}
        />
    </GallerySection>
);
